import { useEffect, useState } from 'react';

// Module-level cache with a short TTL. Flags are live (a flip must take effect
// without a full reload), so the cached map is served immediately and refreshed
// on the next mount once the TTL lapses. Concurrent mounts share one request.
const TTL_MS = 60_000;
let _cache: Record<string, boolean> | null = null;
let _fetchedAt = 0;
let _promise: Promise<Record<string, boolean>> | null = null;

interface FeaturesState {
  features: Record<string, boolean>;
  loading: boolean;
}

export function useFeatures(): FeaturesState {
  const [state, setState] = useState<FeaturesState>({
    features: _cache ?? {},
    loading: _cache === null,
  });

  useEffect(() => {
    let cancelled = false;
    if (_cache !== null && Date.now() - _fetchedAt < TTL_MS) {
      setState({ features: _cache, loading: false });
      return;
    }
    if (!_promise) {
      _promise = fetch('/api/user/features').then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });
    }
    _promise
      .then(f => {
        _cache = f;
        _fetchedAt = Date.now();
        _promise = null;
        if (cancelled) return;
        setState({ features: f, loading: false });
      })
      .catch(() => {
        _promise = null; // allow retry on next mount
        if (cancelled) return;
        // Keep serving the stale map on a failed refresh — a network blip
        // must not hide already-visible navigation.
        setState({ features: _cache ?? {}, loading: false });
      });
    return () => { cancelled = true; };
  }, []);

  return state;
}
